import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Home", href: "#home", id: "home" },
  { label: "Services", href: "#services", id: "services" },
  { label: "Why Us", href: "#why-us", id: "why-us" },
  { label: "Expertise", href: "#expertise", id: "expertise" },
  { label: "Contact", href: "#contact", id: "contact" },
];

const PRODUCTS = [
  { label: "Skillbuddy", sub: "AI life-skills learning companion", href: "/skillbuddy" },
  { label: "PitchPrep", sub: "Prepare & refine your investor pitch", href: "/pitchprep" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleMenu = () => {
    const next = !open;
    setOpen(next);
    if (window.__lenis) {
      next ? window.__lenis.stop() : window.__lenis.start();
    }
  };

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (window.__lenis) window.__lenis.start();
    scrollToId(`#${id}`);
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 border-b backdrop-blur-xl transition-[background-color,box-shadow] duration-500 ${
        scrolled ? "bg-[#001F5C]/95 border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.25)]" : "bg-[#001F5C]/80 border-white/5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <a href="#home" onClick={(e) => go(e, "home")} aria-label="NexOrbit Solutions home" data-testid="nav-logo-link">
          <Logo dark />
        </a>

        <ul className="hidden lg:flex items-center gap-9">
          {LINKS.slice(0, 4).map((l) => (
            <li key={l.id}>
              <a
                href={l.href}
                onClick={(e) => go(e, l.id)}
                data-testid={`nav-link-${l.id}`}
                className="nav-link text-sm font-medium text-white/75 hover:text-white"
              >
                {l.label}
              </a>
            </li>
          ))}
          <li className="relative group">
            <button
              data-testid="nav-products-dropdown"
              className="nav-link flex items-center gap-1.5 text-sm font-medium text-white/75 group-hover:text-white"
            >
              Products
              <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-1/2 top-full -translate-x-1/2 translate-y-2 pt-4 opacity-0 transition-[opacity,transform,visibility] duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
              <div className="w-64 rounded-2xl border border-white/10 bg-[#0A2E73]/95 p-2 shadow-[0_20px_50px_rgba(0,0,0,0.4)] backdrop-blur-xl">
                {PRODUCTS.map((p) => (
                  <a
                    key={p.label}
                    href={p.href}
                    data-testid={`nav-product-${p.label.toLowerCase()}`}
                    className="block rounded-xl px-4 py-3 transition-colors duration-200 hover:bg-white/10"
                  >
                    <span className="block text-sm font-semibold text-white">{p.label}</span>
                    <span className="block text-xs text-white/55">{p.sub}</span>
                  </a>
                ))}
              </div>
            </div>
          </li>
          <li>
            <a
              href="#contact"
              onClick={(e) => go(e, "contact")}
              data-testid="nav-link-contact"
              className="nav-link text-sm font-medium text-white/75 hover:text-white"
            >
              Contact
            </a>
          </li>
        </ul>

        <div className="flex items-center gap-3">
          <button
            onClick={(e) => go(e, "contact")}
            data-testid="nav-schedule-consultation-btn"
            className="hidden lg:inline-flex items-center gap-2 rounded-full bg-nexorange px-5 py-2.5 text-sm font-semibold text-nexblue transition-transform duration-300 hover:scale-[1.04] hover:shadow-[0_8px_24px_rgba(255,149,0,0.35)]"
          >
            Schedule Consultation
            <ArrowUpRight size={16} />
          </button>
          <button
            className="lg:hidden text-white p-2"
            onClick={toggleMenu}
            aria-label="Toggle menu"
            data-testid="nav-hamburger-btn"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden absolute inset-x-0 top-full h-[calc(100vh-72px)] z-50 overflow-y-auto bg-[#001F5C] border-t border-white/10"
            data-testid="mobile-menu"
          >
            <ul className="px-6 py-6 space-y-5">
              {LINKS.map((l) => (
                <li key={l.id}>
                  <a
                    href={l.href}
                    onClick={(e) => go(e, l.id)}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="block text-lg font-medium text-white/85"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li className="pt-2 border-t border-white/10">
                <span className="block text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">Products</span>
                {PRODUCTS.map((p) => (
                  <a
                    key={p.label}
                    href={p.href}
                    data-testid={`mobile-nav-product-${p.label.toLowerCase()}`}
                    className="block py-2 text-lg font-medium text-white/85"
                  >
                    {p.label}
                    <span className="block text-xs text-white/50">{p.sub}</span>
                  </a>
                ))}
              </li>
              <li>
                <button
                  onClick={(e) => go(e, "contact")}
                  data-testid="mobile-schedule-consultation-btn"
                  className="w-full rounded-full bg-nexorange px-5 py-3 text-sm font-semibold text-nexblue"
                >
                  Schedule Consultation
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
